import "./App.css";

import { useEffect } from "react";
import { createPortal } from "react-dom";
import { TamaguiProvider, View } from "tamagui";
import { useLocation, useRoutes } from "react-router-dom";
import { routeConfig } from "./router";

import customConfig from "./tamagui.config";
import { Toaster } from "react-hot-toast";
import { zIndexes } from "@utils/zIndexLevel";
import UtilsProvider from "./provider/UtilsProvider";
import LineLoginProvider from "./provider/LineLoginProvider";
import { bindUnloadFlush, track } from "./lib/tracker";
import CoachPanel from "@screen/Training/CoachPanel";

const containerStyle = {
  width: "100vw",
  height: "100dvh",
  maxWidth: "100%",
  maxHeight: "100%",
  margin: 0,
  padding: 0,
};

/**
 * 全站 toast 外觀：白色膠囊、從頂端下來。
 *
 * 相對 react-hot-toast 預設只縮尺碼：預設是 16px 大字＋20px 打勾動畫，體積接近一張卡片，
 * 蓋在標題列上很搶。改成 13px、細內距的白膠囊，icon 換成 6px 圓點（成功綠／失敗紅）——
 * 狀態用顏色表示就夠，不需要一個和文字等高的圖示。
 * 貼在螢幕最上緣（只留 10px + 安全區），蓋在標題列上方——它只出現兩秒多，
 * 放最上面最快被看到，也不會擋住頁面內容。
 */
const toastDot = (color: string) => (
  <span style={{ width: 6, height: 6, borderRadius: 999, background: color, flexShrink: 0 }} />
);

const toastOptions = {
  duration: 2600,
  style: {
    background: "#fff",
    color: "#0F172A",
    fontSize: 13,
    fontWeight: 600,
    lineHeight: "19px",
    padding: "8px 14px",
    borderRadius: 999,
    maxWidth: "calc(100vw - 48px)",
    boxShadow: "0 6px 20px rgba(15,23,42,0.16)",
  },
  success: { icon: toastDot("#10B981") },
  error: { icon: toastDot("#EF4444"), duration: 3600 },
  blank: { icon: toastDot("#94A3B8") },
};

/** Toast 用 portal 掛到 body，z-index 高於 Sheet，讓 sheet 內觸發的 toast 正常顯示在最上層 */
const ToastPortal = () =>
  createPortal(
    <Toaster
      position="top-center"
      containerStyle={{ zIndex: zIndexes.toastAboveSheet, top: "calc(10px + env(safe-area-inset-top, 0px))" }}
      toastOptions={toastOptions}
    />,
    document.body
  );

/**
 * app_tracker `page_view`：單一掛點觀察路由變化，不要每頁各自埋（見 tracker spec §5.1）。
 * 深連結旗標（例如是否帶 invite code）用「網址上是否有該參數」表示，不解析值本身，避免帶到個資。
 */
function usePageViewTracking() {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    track("page_view", {
      has_invite_param: params.has("invite"),
      has_supplier_id_param: params.has("supplierId"),
    });
  }, [location.pathname, location.search]);

  useEffect(() => {
    bindUnloadFlush();
  }, []);
}

function App() {
  const element = useRoutes(routeConfig);
  usePageViewTracking();

  return (
    <TamaguiProvider config={customConfig} defaultTheme="light" disableInjectCSS={false}>
      <LineLoginProvider>
        <UtilsProvider>
          <View {...containerStyle} overflow="hidden">
            {element}
          </View>
        </UtilsProvider>
        {/* 客服練習模式：教練面板（練習中的每一步）；內部判斷是否練習中 */}
        <CoachPanel />
      </LineLoginProvider>
      <ToastPortal />
    </TamaguiProvider>
  );
}

export default App;
